/**
 * Transient dims written through `_visibility` (R11).
 *
 * Hover emphasis, edge focus and edge de-emphasis used to dim elements with
 * CSS classes that carried their own `opacity`. R11 says opacity lives in one
 * channel, the `_visibility` data property, so these interaction states write
 * it too. Because they are short-lived they must hand the previous value back
 * afterwards, which is what the per-tag hold key is for.
 *
 * These writes happen on the live Cytoscape instance only (they never touch
 * `useGraphStore.elements`), so a transient dim cannot leak into a saved
 * snapshot or an undo step. Persistent dims (filters, Follow Money, SSE
 * discovery) still go through `canvasController.dim()`.
 *
 * Presence is not handled here: hiding an element means `canvasController.stash`,
 * never a `_visibility` of 0.
 */
import { VISIBILITY } from '../constants/visibility';

/** Tags for the interaction states that use this helper. */
export type TransientTag = 'hover' | 'focus' | 'deemphasis' | 'find';

const holdKey = (tag: TransientTag) => `_visHold_${tag}`;

interface Dimmable {
    data(key: string): any;
    data(key: string, value: any): any;
    removeData(key: string): any;
}

/**
 * Dim a collection to `level`. Never raises an element that is already dimmer.
 * Re-applying the same tag keeps the originally held value.
 */
export function dimTransient(
    eles: { forEach(cb: (ele: Dimmable) => void): void },
    level: number,
    tag: TransientTag,
): void {
    if (!(level > 0 && level <= 1)) return; // stash, never 0
    const key = holdKey(tag);
    eles.forEach((ele) => {
        const held = ele.data(key);
        const current = typeof ele.data('_visibility') === 'number' ? ele.data('_visibility') : VISIBILITY.VISIBLE;
        const base = typeof held === 'number' ? held : current;
        if (typeof held !== 'number') ele.data(key, base);
        ele.data('_visibility', Math.min(base, level));
    });
}

/** Give back whatever `dimTransient` held for this tag. */
export function clearTransient(
    eles: { forEach(cb: (ele: Dimmable) => void): void },
    tag: TransientTag,
): void {
    const key = holdKey(tag);
    eles.forEach((ele) => {
        const held = ele.data(key);
        if (typeof held !== 'number') return;
        ele.data('_visibility', held);
        ele.removeData(key);
    });
}
